import React, { useState } from 'react';
import {
  Box,
  Container,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Link,
  Card,
  CardContent
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/authService';
import logo from '../assets/logo.png';
import industrialImage from '../assets/industrial-graphics.jpeg';

const NewLoginPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    emailOrPhone: '',
    password: '',
    otp: ''
  });
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [credentialsVerified, setCredentialsVerified] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!formData.emailOrPhone || !formData.password) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      // First verify credentials and send OTP
      if (!otpSent) {
        await authService.sendLoginOTP(formData.emailOrPhone, formData.password);
        setOtpSent(true);
        setCredentialsVerified(true);
        setSuccess('Credentials verified! OTP sent to your email/SMS.');
        setError('');
        setLoading(false);
        return;
      }

      // Verify OTP and complete login
      if (!formData.otp) {
        setError('Please enter the OTP');
        setLoading(false);
        return;
      }

      const response = await authService.verifyLogin(
        formData.emailOrPhone,
        formData.password,
        formData.otp
      );
      
      localStorage.setItem('authToken', response.token || response.access);
      localStorage.setItem('userInfo', JSON.stringify(response.user));
      
      if (response.user.is_superuser) {
        navigate('/admin');
      } else if (response.user.is_staff) {
        navigate('/staff');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleLoginWithoutOTP = async () => {
    setLoading(true);
    try {
      const response = await authService.simpleLogin(formData.emailOrPhone, formData.password);
      
      localStorage.setItem('authToken', response.token);
      localStorage.setItem('userInfo', JSON.stringify(response.user));
      
      if (response.user.is_superuser) {
        navigate('/admin');
      } else if (response.user.is_staff) {
        navigate('/staff');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      setError('Login without OTP failed: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        p: 3,
        bgcolor: 'white'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <img src={logo} alt="TICSPL" style={{ height: '40px', marginRight: '12px' }} />
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
            TICSPL Industrial Portal
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button 
            variant="outlined" 
            onClick={() => navigate('/register')}
            sx={{ borderRadius: 2 }}
          >
            Register
          </Button>
          <Button 
            variant="contained" 
            sx={{ 
              bgcolor: '#1e40af', 
              borderRadius: 2,
              '&:hover': { bgcolor: '#1d4ed8' }
            }}
          >
            Login
          </Button>
        </Box>
      </Box>

      <Box sx={{ position: 'relative', px: 12, minHeight: '70vh', pt: 6 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', gap: 6 }}>
          {/* Left Side - Content */}
          <Box sx={{ flex: 1, maxWidth: '45%' }}>
            <Typography 
              variant="h2" 
              sx={{ 
                fontWeight: 700, 
                color: '#1e293b', 
                mb: 2,
                fontSize: { xs: '3rem', md: '4.5rem' },
                lineHeight: 1.1
              }}
            >
              Facilitating<br />Industrial<br />Establishment
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                color: '#64748b', 
                mb: 3, 
                lineHeight: 1.4,
                fontSize: '1.3rem',
                maxWidth: '400px'
              }}
            >
              Empowering entrepreneurs with comprehensive resources and expert guidance to establish and grow successful industrial ventures.
            </Typography>
          </Box>

          {/* Right Side - Login Form */}
          <Box sx={{ flex: 1, maxWidth: '55%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            {/* Login Form */}
            <Card sx={{ boxShadow: 2, width: 450 }}>
              {/* Industrial Graphics at top of card */}
              <Box sx={{ 
                width: '100%', 
                height: 180, 
                overflow: 'hidden'
              }}>
                <img 
                  src={industrialImage} 
                  alt="Industrial Graphics" 
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: 'center'
                  }}
                />
              </Box>
              <CardContent sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5, textAlign: 'center', fontSize: '1.1rem' }}>
                  Login
                </Typography>
                
                {error && <Alert severity="error" sx={{ mb: 1, py: 0.3, fontSize: '0.8rem' }}>{error}</Alert>}
                {success && <Alert severity="success" sx={{ mb: 1, py: 0.3, fontSize: '0.8rem' }}>{success}</Alert>}
                
                <Box component="form" onSubmit={handleLogin}>
                  <TextField
                    fullWidth
                    name="emailOrPhone"
                    label="Email Address"
                    value={formData.emailOrPhone}
                    onChange={handleInputChange}
                    required
                    disabled={loading}
                    size="small"
                    sx={{ mb: 1.2 }}
                  />
                  
                  <TextField
                    fullWidth
                    name="password"
                    type="password"
                    label="Password"
                    value={formData.password}
                    onChange={handleInputChange}
                    required
                    disabled={loading}
                    size="small"
                    sx={{ mb: 1.5 }}
                  />
                  
                  {otpSent && (
                    <TextField
                      fullWidth
                      name="otp"
                      label="Enter OTP"
                      value={formData.otp}
                      onChange={handleInputChange}
                      required
                      disabled={loading}
                      size="small"
                      sx={{ mb: 1.2 }}
                      helperText="Check your email/SMS for the OTP"
                    />
                  )}
                  
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={loading}
                    sx={{
                      bgcolor: '#1e40af',
                      py: 1,
                      fontSize: '0.85rem',
                      fontWeight: 600,
                      mb: 1.2,
                      '&:hover': { bgcolor: '#1d4ed8' }
                    }}
                  >
                    {loading ? <CircularProgress size={18} color="inherit" /> : (otpSent ? 'Verify OTP & Login' : 'Send OTP & Login')}
                  </Button>
                  
                  {credentialsVerified && (
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={handleLoginWithoutOTP}
                      disabled={loading}
                      sx={{
                        borderColor: '#10b981',
                        color: '#10b981',
                        py: 1,
                        fontSize: '0.85rem',
                        fontWeight: 600,
                        mb: 1.2,
                        '&:hover': { 
                          borderColor: '#059669',
                          color: '#059669',
                          bgcolor: '#f0fdf4'
                        }
                      }}
                    >
                      Login without OTP (Testing)
                    </Button>
                  )}
                  
                  <Box sx={{ textAlign: 'center', mb: 1.5 }}>
                    <Link 
                      component="button" 
                      type="button" 
                      onClick={() => navigate('/forgot-password')}
                      sx={{ 
                        color: '#1e40af', 
                        fontWeight: 500,
                        fontSize: '0.85rem',
                        textDecoration: 'none',
                        '&:hover': { textDecoration: 'underline' }
                      }}
                    >
                      Forgot Password?
                    </Link>
                  </Box>
                  
                  <Typography variant="body2" sx={{ textAlign: 'center', color: '#64748b', fontSize: '0.85rem' }}>
                    Dont have an account?{' '}
                    <Link 
                      component="button" 
                      type="button" 
                      onClick={() => navigate('/register')}
                      sx={{ color: '#1e40af', fontWeight: 600 }}
                    >
                      Register
                    </Link>
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>
      </Box>
        
      <Container maxWidth="xl" sx={{ py: 2, px: 16 }}>
        {/* How It Works Section */}
        <Box sx={{ mt: 3, textAlign: 'left' }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 4, color: '#1e293b', textAlign: 'left', ml: 9 }}>
            How It Works
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center', mb: 3, px: 1 }}>
            {[
              { icon: '👥', title: 'Register', desc: 'Get expert guidance through every step of your industrial establishment journey.' },
              { icon: '🔍', title: 'Explore Schemes', desc: 'Access subsidies and schemes to support your industrial projects financially.' },
              { icon: '🏭', title: 'Start Projects', desc: 'Create projects and collaborate with us for comprehensive support in building your enterprise.' },
              { icon: '📋', title: 'Track Progress', desc: 'Easily manage and submit necessary documents for compliance and approval.' }
            ].map((item, index) => (
              <React.Fragment key={index}>
                <Box sx={{ 
                  textAlign: 'center', 
                  px: 1.5, 
                  py: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  flex: '1 1 25%'
                }}>
                  <Box sx={{ 
                    width: 60, 
                    height: 60, 
                    borderRadius: '50%', 
                    bgcolor: '#3b82f6', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 2,
                    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
                  }}>
                    <Typography sx={{ fontSize: '1.5rem', color: 'white' }}>{item.icon}</Typography>
                  </Box>
                  <Typography variant="subtitle1" sx={{ 
                    fontWeight: 600, 
                    mb: 1.5, 
                    fontSize: '1rem',
                    color: '#1e293b'
                  }}>
                    {item.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ 
                    fontSize: '0.85rem', 
                    lineHeight: 1.4,
                    textAlign: 'center',
                    maxWidth: '100%'
                  }}>
                    {item.desc}
                  </Typography>
                </Box>
                {index < 3 && (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    mt: 4,
                    mx: 1
                  }}>
                    <Typography sx={{ fontSize: '1.5rem', color: '#3b82f6' }}>→</Typography>
                  </Box>
                )}
              </React.Fragment>
            ))}
          </Box>
        </Box>
        

      </Container>
    </Box>
  );
};

export default NewLoginPage;